import React, { useMemo, useState, memo } from 'react';
import { PageLayout } from '../shared/PageLayout';
import { StatCard } from '../shared/StatCard';
import { Server, Clock, HardDrive, ToggleRight, ToggleLeft, Activity } from 'lucide-react';
import { isStrictMode, setStrictMode } from '../../../../utils/strictMode';
import { ALL_QUESTIONS_CORE } from '../../../../data/questionLoader';
import { useAppStore } from '../../../../hooks/useAppStore';
import { computeLiveSystemStats } from '../../../../governance/systemStats';
export function ConsolePage() {
  const [strictMode, setStrictModeState] = useState(isStrictMode());
  const {
    accounts
  } = useAppStore();
  const handleToggleStrictMode = () => {
    const next = !strictMode;
    setStrictMode(next);
    setStrictModeState(next);
  };
  const diagnostics = useMemo(() => {
    const systemStats = computeLiveSystemStats();
    let questions: any[] = [];
    let questionLoadError = false;
    try {
      questions = ALL_QUESTIONS_CORE as any[];
    } catch {
      questionLoadError = true;
    }
    // Calculate real storage usage
    let storageUsed = 0;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          storageUsed += localStorage.getItem(key)?.length || 0;
        }
      }
    } catch (e) {
      // Ignore
    }
    const storageMb = (storageUsed / 1024 / 1024).toFixed(2);
    // Calculate active sessions (active in last 1 hour)
    const activeAccounts = accounts?.accountsById ? Object.values(accounts.accountsById).filter(a => {
      return Date.now() - (a.lastActive || 0) < 60 * 60 * 1000;
    }).length : 0;
    // Mock response time based on performance API if available
    let responseTime = 45;
    try {
      if (window.performance && window.performance.timing) {
        responseTime = window.performance.timing.responseEnd - window.performance.timing.requestStart;
      }
    } catch (e) {
      // Ignore
    }
    return {
      systemStats,
      questions,
      questionLoadError,
      storageMb,
      activeAccounts,
      responseTime: Math.max(10, responseTime)
    };
  }, [accounts]);
  const resourceMetrics = useMemo(() => {
    let memoryUsage = 0;
    try {
      if ((window.performance as any)?.memory) {
        const mem = (window.performance as any).memory;
        memoryUsage = Math.round(mem.usedJSHeapSize / mem.jsHeapSizeLimit * 100);
      } else {
        // Fallback estimate based on storage
        memoryUsage = Math.min(100, Math.round(parseFloat(diagnostics.storageMb) / 5 * 100));
      }
    } catch (e) {
      // Ignore
    }
    const storageUsage = Math.min(100, Math.round(parseFloat(diagnostics.storageMb) / 5 * 100));
    return {
      memory: memoryUsage,
      storage: storageUsage
    };
  }, [diagnostics.storageMb]);
  // Generate real system events based on current state
  const SYSTEM_EVENTS = useMemo(() => {
    const events = [];
    const now = new Date();
    events.push({
      time: now.toLocaleTimeString('nl-NL', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      level: 'info',
      source: 'System',
      message: 'Admin dashboard geladen'
    });
    if (strictMode) {
      events.push({
        time: new Date(now.getTime() - 5000).toLocaleTimeString('nl-NL', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }),
        level: 'info',
        source: 'System',
        message: 'Strict Mode is actief'
      });
    }
    events.push({
      time: new Date(now.getTime() - 15000).toLocaleTimeString('nl-NL', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      level: 'info',
      source: 'Seeds',
      message: `Seed consolidatie voltooid: ${diagnostics.questions.length} vragen geladen`
    });
    if (diagnostics.questionLoadError) {
      events.push({
        time: new Date(now.getTime() - 20000).toLocaleTimeString('nl-NL', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }),
        level: 'error',
        source: 'System',
        message: 'Fout bij het laden van vragen'
      });
    }
    const activeAccounts = accounts?.accountsById ? Object.values(accounts.accountsById).filter(a => {
      // Active in last 24h
      return Date.now() - a.lastActive < 24 * 60 * 60 * 1000;
    }).length : 0;
    events.push({
      time: new Date(now.getTime() - 3600000).toLocaleTimeString('nl-NL', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      level: 'info',
      source: 'Session',
      message: `${activeAccounts} actieve gebruikers vandaag`
    });
    return events;
  }, [strictMode, diagnostics, accounts]);
  return <PageLayout title="Console" subtitle="Systeemstatus en configuratie" breadcrumbs={[{
    label: 'Systeembeheer'
  }, {
    label: 'Console'
  }]} data-id="element-773">
      <div className="space-y-6" data-id="element-774">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-775">
          <StatCard label="Uptime" value="N/A" subtitle="Client-side app" icon={<Server className="w-5 h-5" data-id="element-777" />} color="emerald" data-id="element-776" />
          <StatCard label="Laadtijd" value={`${diagnostics.responseTime}ms`} subtitle="gemiddeld" icon={<Clock className="w-5 h-5" data-id="element-779" />} color="amber" data-id="element-778" />
          <StatCard label="Opslag" value={`${diagnostics.storageMb} MB`} subtitle="Lokale opslag" icon={<HardDrive className="w-5 h-5" data-id="element-781" />} color="blue" data-id="element-780" />
          <StatCard label="Actieve Sessies" value={diagnostics.activeAccounts} subtitle="Laatste uur" icon={<Activity className="w-5 h-5" data-id="element-783" />} color="purple" data-id="element-782" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-784">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" data-id="element-785">
            <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-786">
              Systeem Configuratie
            </h3>
            <div className="space-y-4" data-id="element-787">
              <div className={`p-4 rounded-xl border ${strictMode ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`} data-id="element-788">
                <div className="flex items-center justify-between" data-id="element-789">
                  <div data-id="element-790">
                    <p className="text-sm font-semibold text-gray-900" data-id="element-791">
                      Strict Mode
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-792">
                      Blokkeert sessies bij validatiefouten
                    </p>
                  </div>
                  <button onClick={handleToggleStrictMode} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${strictMode ? 'bg-amber-600 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-793">
                    {strictMode ? <ToggleRight className="w-4 h-4" data-id="element-794" /> : <ToggleLeft className="w-4 h-4" data-id="element-795" />}
                    {strictMode ? 'AAN' : 'UIT'}
                  </button>
                </div>
                {strictMode && <p className="text-xs text-amber-700 mt-2 bg-amber-100 px-3 py-1.5 rounded-lg" data-id="element-796">
                    ⚠ Strict Mode is actief. Leerlingen kunnen geen sessies
                    starten bij validatiefouten.
                  </p>}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" data-id="element-797">
            <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-798">
              Systeembronnen
            </h3>
            <div className="space-y-4" data-id="element-799">
              <ResourceBar label="CPU" value="N/A" color="bg-blue-500" data-id="element-800" />
              <ResourceBar label="Geheugen" value={resourceMetrics.memory} color="bg-purple-500" data-id="element-801" />
              <ResourceBar label="Opslag" value={resourceMetrics.storage} color="bg-green-500" data-id="element-802" />
              <ResourceBar label="Netwerk" value="N/A" color="bg-orange-500" data-id="element-803" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-804">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-805">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-806">
              Recente Systeemgebeurtenissen
            </h3>
            <span className="text-xs text-gray-400" data-id="element-807">Laatste 10 entries</span>
          </div>
          <div className="divide-y divide-gray-50" data-id="element-808">
            {SYSTEM_EVENTS.map((event, i) => <div key={i} className="px-5 py-2.5 flex items-start gap-4 hover:bg-gray-50/50" data-id="element-809">
                <span className="text-xs text-gray-400 font-mono w-16 flex-shrink-0 mt-0.5" data-id="element-810">
                  {event.time}
                </span>
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${event.level === 'error' ? 'bg-red-100 text-red-700' : event.level === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-811">
                  {event.level}
                </span>
                <span className="text-xs text-gray-500 w-16 flex-shrink-0" data-id="element-812">
                  {event.source}
                </span>
                <span className="text-sm text-gray-700" data-id="element-813">{event.message}</span>
              </div>)}
          </div>
        </div>
      </div>
    </PageLayout>;
}
function ResourceBar({
  label,
  value,
  color
}: {
  label: string;
  value: number | string;
  color: string;
}) {
  const isNumber = typeof value === 'number';
  const displayValue = isNumber ? `${value}%` : value;
  const percentValue = isNumber ? value : 0;
  return <div data-id="element-814">
      <div className="flex justify-between text-xs mb-1.5" data-id="element-815">
        <span className="text-gray-700 font-medium" data-id="element-816">{label}</span>
        <span className={`font-semibold ${isNumber && value > 80 ? 'text-red-500' : isNumber && value > 60 ? 'text-yellow-600' : 'text-gray-600'}`} data-id="element-817">
          {displayValue}
        </span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-818">
        <div className={`${color} h-2 rounded-full transition-all`} style={{
        width: `${percentValue}%`
      }} data-id="element-819" />
      </div>
    </div>;
}